import { useEffect, useRef } from "react";

/** Two rising notes, synthesised so the display ships no audio file. */
const NOTES_HZ = [880, 1174.66];
const NOTE_SECONDS = 0.18;
const NOTE_GAP_SECONDS = 0.12;
const PEAK_GAIN = 0.2;

let context: AudioContext | null = null;

const audioContext = (): AudioContext | null => {
  if (typeof AudioContext === "undefined") return null;
  context ??= new AudioContext();
  return context;
};

const playChime = (ctx: AudioContext) => {
  NOTES_HZ.forEach((hz, i) => {
    const at = ctx.currentTime + i * NOTE_GAP_SECONDS;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = hz;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, at + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, at + NOTE_SECONDS);
    osc.connect(gain).connect(ctx.destination);
    osc.start(at);
    osc.stop(at + NOTE_SECONDS);
  });
};

/**
 * Chimes once each time a different question appears on the screen. Browsers
 * keep audio suspended until the page has seen a gesture, so the first tap on
 * the display after pairing unlocks it; a chime before that is dropped rather
 * than held for the tap.
 */
export const useQuestionChime = (questionId: string | null) => {
  const heard = useRef<string | null>(null);

  useEffect(() => {
    const unlock = () => void audioContext()?.resume();
    document.addEventListener("pointerdown", unlock, { once: true });
    return () => document.removeEventListener("pointerdown", unlock);
  }, []);

  useEffect(() => {
    if (questionId === heard.current) return;
    heard.current = questionId;
    if (questionId === null) return;
    const ctx = audioContext();
    if (ctx?.state === "running") playChime(ctx);
  }, [questionId]);
};
